/** @jsxImportSource @emotion/react */
"use client";
import { useTranslations } from "@i18n/react";
import { useConsent } from "@packages/consent";
import { useRequestAuth, uuid } from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";
import AppleSignin from "react-apple-signin-auth";

import { Button } from "../../_base/_core/Button/Button.emotion";
import { Apple2Icon } from "../../_base/icons/Icon/ExternalIcons";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import type { AuthSocialButtonProps } from "./AuthTypes.emotion";

export const AppleAuthButton = ({ onAuth }: AuthSocialButtonProps) => {
  const t = useTranslations();
  const alert = useAlert();
  const { mutationAppleAuth } = useRequestAuth();
  const { voluntaryConsent } = useConsent();
  const completeAppleAuth = async (res) => {
    const token = res.authorization.code;
    const name = res?.user?.name?.firstName ?? null;
    const last_name = res?.user?.name?.lastName ?? null;

    try {
      const data = await mutationAppleAuth.mutateAsync({
        token,
        name,
        last_name,
        consent: voluntaryConsent ?? undefined,
        redirect: process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI,
      });
      await onAuth?.({ data });
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  return (
    <AppleSignin
      authOptions={{
        clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID,
        scope: process.env.NEXT_PUBLIC_APPLE_SCOPE,
        redirectURI: process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI,
        state: uuid(),
        usePopup: true,
      }}
      uiType="dark"
      onSuccess={(res) => completeAppleAuth(res)}
      onError={async () => {
        await alert.show({
          title: t("general_word_error"),
          description: t("login_social_auth_error_message"),
        });
      }}
      render={(props) => (
        <Button
          {...props}
          size="l"
          variant="neutral"
          startIcon={<Apple2Icon />}
          isFullWidth
          data-testid={HallowDataTestId.AuthContinueWithAppleButton}
        >
          {t("launch_continue_with_apple")}
        </Button>
      )}
    />
  );
};

AppleAuthButton.displayName = "AppleAuthButton";
